// =============================================================================
// us-english
// -----------------------------------------------------------------------------
// Converts UK copy to US copy for the /us locale. Two jobs:
//
//   1. Spelling. STEMS below is an explicit list rather than a general
//      -ise/-ize rule, because plenty of -ise words (advise, exercise,
//      expertise, premises, enterprise) are correct on both sides.
//   2. Terminology. UK practices say "PMS" (practice management system);
//      US practices say "PIMS" (practice information management system).
//
// Anything inside double quotation marks is left exactly as written, so an
// attributed quote keeps whatever the person actually said. In HTML the quote
// state carries across tags, so a quote broken up by <em> or <strong> is still
// protected end to end.
// =============================================================================

/**
 * British stem → American stem. Lowercase only; the capitalised variant of
 * each pair is generated below. Order matters, so longer/more specific stems
 * come first. Lookaheads guard words that only look British:
 * organist, optimism, realistic, Greyhound.
 */
const STEMS: Array<[string, string]> = [
  // -ise / -isation → -ize / -ization
  ['apologis', 'apologiz'],
  ['authoris', 'authoriz'],
  ['categoris', 'categoriz'],
  ['centralis', 'centraliz'],
  ['computeris', 'computeriz'],
  ['criticis(?!m)', 'criticiz'],
  ['customis', 'customiz'],
  ['digitis', 'digitiz'],
  ['familiaris', 'familiariz'],
  ['finalis', 'finaliz'],
  ['formalis', 'formaliz'],
  ['harmonis', 'harmoniz'],
  ['hospitalis', 'hospitaliz'],
  ['incentivis', 'incentiviz'],
  ['itemis', 'itemiz'],
  ['legitimis', 'legitimiz'],
  ['localis', 'localiz'],
  ['maximis', 'maximiz'],
  ['minimis', 'minimiz'],
  ['modernis', 'moderniz'],
  ['monetis', 'monetiz'],
  ['normalis', 'normaliz'],
  ['personalis', 'personaliz'],
  ['prioritis', 'prioritiz'],
  ['rationalis', 'rationaliz'],
  ['recognis', 'recogniz'],
  ['scrutinis', 'scrutiniz'],
  ['stabilis', 'stabiliz'],
  ['standardis', 'standardiz'],
  ['sterilis', 'steriliz'],
  ['subsidis', 'subsidiz'],
  ['summaris', 'summariz'],
  ['synchronis', 'synchroniz'],
  ['systematis', 'systematiz'],
  ['utilis', 'utiliz'],
  ['visualis', 'visualiz'],
  ['specialis(?!t)', 'specializ'],
  ['organis(?!t|m)', 'organiz'],
  ['optimis(?!t|m)', 'optimiz'],
  ['realis(?!m|t)', 'realiz'],

  // Spelt out rather than stemmed, so the nouns "analysis" and "emphasis"
  // are left alone.
  ['analyser', 'analyzer'],
  ['analysing', 'analyzing'],
  ['analysed', 'analyzed'],
  ['analyse', 'analyze'],
  ['emphasising', 'emphasizing'],
  ['emphasised', 'emphasized'],
  ['emphasise', 'emphasize'],

  // -our → -or
  ['behaviour', 'behavior'],
  ['colour', 'color'],
  ['endeavour', 'endeavor'],
  ['favour', 'favor'],
  ['flavour', 'flavor'],
  ['harbour', 'harbor'],
  ['honour', 'honor'],
  ['humour', 'humor'],
  ['labour', 'labor'],
  ['neighbour', 'neighbor'],
  ['odour', 'odor'],
  ['rumour', 'rumor'],
  ['tumour', 'tumor'],
  ['vapour', 'vapor'],

  // -re → -er
  ['calibre', 'caliber'],
  ['centred', 'centered'],
  ['centring', 'centering'],
  ['centre', 'center'],
  ['fibre', 'fiber'],
  ['litre', 'liter'],
  ['manoeuvr', 'maneuver'],
  ['metre', 'meter'],
  ['theatre', 'theater'],
  ['titre', 'titer'],

  // -ce → -se
  ['defence', 'defense'],
  ['licence', 'license'],
  ['offence', 'offense'],
  ['practis', 'practic'],
  ['pretence', 'pretense'],

  // -ae- / -oe- → -e- (mostly clinical)
  ['anaesthetis', 'anesthetiz'],
  ['anaesthe', 'anesthe'],
  ['anaemi', 'anemi'],
  ['caesarean', 'cesarean'],
  ['coeliac', 'celiac'],
  ['diarrhoea', 'diarrhea'],
  ['faec', 'fec'],
  ['foetal', 'fetal'],
  ['foetus', 'fetus'],
  ['gynaecolog', 'gynecolog'],
  ['haemat', 'hemat'],
  ['haemorrh', 'hemorrh'],
  ['leukaemi', 'leukemi'],
  ['oedema', 'edema'],
  ['oesophag', 'esophag'],
  ['oestr', 'estr'],
  ['orthopaed', 'orthoped'],
  ['paediatr', 'pediatr'],

  // Doubled consonants the US drops
  ['cancelled', 'canceled'],
  ['cancelling', 'canceling'],
  ['counsell', 'counsel'],
  ['fuell', 'fuel'],
  ['kennell', 'kennel'],
  ['labell', 'label'],
  ['levell', 'level'],
  ['marvellous', 'marvelous'],
  ['modell', 'model'],
  ['signall', 'signal'],
  ['travell', 'travel'],
  ['enrolment', 'enrollment'],
  ['fulfilment', 'fulfillment'],
  ['instalment', 'installment'],
  ['skilful', 'skillful'],
  ['wilful', 'willful'],

  // Everything else
  ['acknowledgement', 'acknowledgment'],
  ['ageing', 'aging'],
  ['aluminium', 'aluminum'],
  ['amongst', 'among'],
  ['backwards', 'backward'],
  ['burnt', 'burned'],
  ['catalogue', 'catalog'],
  ['cheque', 'check'],
  ['draught', 'draft'],
  ['grey(?!hound)', 'gray'],
  ['judgement', 'judgment'],
  ['learnt', 'learned'],
  ['mould', 'mold'],
  ['per cent', 'percent'],
  ['programme', 'program'],
  ['sceptic', 'skeptic'],
  ['specialities', 'specialties'],
  ['speciality', 'specialty'],
  ['sulphur', 'sulfur'],
  ['towards', 'toward'],
  ['whilst', 'while'],
]

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

const BRIT_TO_AM: Array<[RegExp, string]> = STEMS.flatMap(
  ([uk, am]): Array<[RegExp, string]> => [
    [new RegExp(uk, 'g'), am],
    [new RegExp(capitalise(uk), 'g'), capitalise(am)],
  ],
)

/** Applies US spellings only. Does not touch terminology. */
export function americanizeSpelling(s: string): string {
  let out = s
  for (const [re, rep] of BRIT_TO_AM) out = out.replace(re, rep)
  return out
}

/** US spellings plus PMS → PIMS. Assumes the input is not inside a quote. */
function toUs(s: string): string {
  return americanizeSpelling(s).replace(/\bPMS\b/g, 'PIMS')
}

const QUOTE = /(["“”])/

/**
 * Applies `toUs` to `html`, skipping tags and anything between quotation
 * marks. Quote state is tracked across the whole string, so a quote split by
 * an inline tag stays protected. An unclosed quote leaves the rest of the
 * string untouched, which is the safe direction to fail in.
 */
export function toUsCopy(html: string): string {
  let inQuote = false
  return html.replace(/<[^>]+>|[^<]+/g, (token) => {
    if (token.startsWith('<')) return token
    return token
      .split(QUOTE)
      .map((part) => {
        if (QUOTE.test(part) && part.length === 1) {
          inQuote = !inQuote
          return part
        }
        return inQuote ? part : toUs(part)
      })
      .join('')
  })
}

/** Recursively applies `toUsCopy` to every string in a value. */
export function toUsCopyDeep<T>(value: T): T {
  if (typeof value === 'string') return toUsCopy(value) as T
  if (Array.isArray(value)) return value.map(toUsCopyDeep) as unknown as T
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const k in value as Record<string, unknown>) {
      out[k] = toUsCopyDeep((value as Record<string, unknown>)[k])
    }
    return out as T
  }
  return value
}
